import { SuggestActionForSingleFileCannotShareText } from './refreshSharingTipsAttachmentWell.locstring.json';
import loc from 'owa-localize';
import { refreshSharingTipsAttachmentWell } from '../../actions/publicActions';
import AttachmentWellViewState from '../../schema/AttachmentWellViewState';
import getAttachmentFullViewStateById from '../../utils/getAttachmentFullViewStateById';
import replaceAttachment from '../../utils/replaceAttachment';
import { getSucceesfulSharingDataFromAttachments } from '../../utils/sharingTips/getSucceesfulSharingDataFromAttachments';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { ClientAttachmentId } from 'owa-client-ids';
import {
    InfoBarCustomAction,
    InfoBarMessageViewState,
    InfoBarHostViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import addInfoBarMessage from 'owa-info-bar/lib/actions/addInfoBarMessage';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';

import { orchestrator } from 'satcheljs';
import {
    AttachmentFullViewState,
    convertCloudyToClassic,
    ConvertCloudyToClassicSource,
} from 'owa-attachment-full-data';
import {
    getSharingTipIdsList,
    IdType,
    lazyGetInfoBarCreator,
    SharingData,
    SharingTipId,
    SharingTipRecipientInfo,
} from 'owa-sharing-data';

// TODO: VSO: 32525 - move this file to owa-sharing-data (or owa-mail-compose-store?)
// Ideally the only input should be an Id.
orchestrator(refreshSharingTipsAttachmentWell, async actionMessage => {
    const {
        attachmentWellViewState,
        parentItemId,
        recipientInfo,
        infoBarIds,
        infoBar,
    } = actionMessage;

    // We only want to calculate sharing tips if there is sharing data from a success response
    const sharingData: SharingData[] = getSucceesfulSharingDataFromAttachments(
        attachmentWellViewState
    );

    const datapoint = new PerformanceDatapoint('AttachmentRefreshSharingTips');

    // TODO VSO 32530 - refresh sharing tips should only make a sharing tip, and know if it needs an action or not. The
    // action should be a literal action that gets fired off, and whoever wanted to refresh the sharing tips should consume it.
    const convertCloudyToClassicInfoBarAction = (attachmentId: ClientAttachmentId) => {
        const currentAttachment: AttachmentFullViewState = getAttachmentFullViewStateById(
            attachmentWellViewState,
            attachmentId
        );
        if (currentAttachment) {
            const attachment = getAttachment(currentAttachment.attachmentId);
            const infoBarCustomAction: InfoBarCustomAction = {
                text: loc(SuggestActionForSingleFileCannotShareText),
                action: () => {
                    // TODO: Make this a literal action, leave the logic below as an orcestrator in AW. Move the rest of the infobar logic out to compose.
                    const referenceAttachment: ReferenceAttachment = attachment.model as ReferenceAttachment;
                    logUsage('SharingTipsConvertToClassic');
                    convertCloudyToClassic(
                        <AttachmentFullViewState>currentAttachment,
                        parentItemId.Id,
                        referenceAttachment.AttachLongPathName,
                        referenceAttachment.PermissionType,
                        (
                            attachmentViewState: AttachmentFullViewState,
                            attachmentType: AttachmentType,
                            isReadOnly: boolean,
                            uploadCompleted: boolean
                        ) => {
                            replaceAttachment(
                                attachmentWellViewState,
                                attachmentViewState,
                                attachmentType,
                                isReadOnly,
                                uploadCompleted
                            );
                        },
                        ConvertCloudyToClassicSource.sharingTip
                    );
                },
            };
            return infoBarCustomAction;
        }
        return null;
    };

    const sharingTipIds = getSharingTipIdsList();

    const newSharingTipsPassedByReferenceAndModified: string[] = [];

    const getInfoBarCreator = await lazyGetInfoBarCreator.import();
    refreshSharingTipsAndReturnLastSharingIssue(
        sharingTipIds,
        sharingData,
        recipientInfo,
        newSharingTipsPassedByReferenceAndModified,
        infoBar,
        getInfoBarCreator,
        false /* skipActionRecommendation */,
        convertCloudyToClassicInfoBarAction
    );

    logSharingTipsDatapoints(infoBarIds, newSharingTipsPassedByReferenceAndModified);
    Promise.resolve().then(() => datapoint.end());
});

export function getLastCloudyAttachmentsSharingIssueForBlockOnSend(
    sharingTipIds: SharingTipId[],
    attachmentWellViewState: AttachmentWellViewState,
    recipientInfo: SharingTipRecipientInfo[],
    getInfoBarCreator: (
        sharingData: SharingData[],
        sharingTipId: SharingTipId,
        recipientInfo: SharingTipRecipientInfo[],
        skipActionRecommendation: boolean,
        convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
    ) => InfoBarMessageViewStateCreator | null
): string {
    const sharingData: SharingData[] = getSucceesfulSharingDataFromAttachments(
        attachmentWellViewState
    );
    return refreshSharingTipsAndReturnLastSharingIssue(
        sharingTipIds,
        sharingData,
        recipientInfo,
        [] /*newSharingTipsPassedByReferenceAndModified*/,
        null /*infoBar*/,
        getInfoBarCreator,
        true /*skipActionRecommendation*/,
        null /*constCloudyToClassicAction*/
    );
}
function refreshSharingTipsAndReturnLastSharingIssue(
    sharingTipIds: SharingTipId[],
    sharingData: SharingData[],
    recipientInfo: SharingTipRecipientInfo[],
    newSharingTipsPassedByReferenceAndModified: string[],
    infoBar: InfoBarHostViewState,
    getInfoBarCreator: (
        sharingData: SharingData[],
        sharingTipId: SharingTipId,
        recipientInfo: SharingTipRecipientInfo[],
        skipActionRecommendation: boolean,
        convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
    ) => InfoBarMessageViewStateCreator | null,
    skipActionRecommendation: boolean,
    convertCloudyToClassicAction: (id: ClientAttachmentId) => InfoBarCustomAction
): string {
    // TODO 32530: we are always passing in convertCloudyToClassic as the custom action. This is a bandaid to maintain the fuctionality of attaching as a copy instead
    // While making this entire feature more generic.
    let lastInfoBarMessageViewStateCreator: InfoBarMessageViewStateCreator;
    for (let i: number = 0; i < sharingTipIds.length; i++) {
        const sharingTipId = sharingTipIds[i];
        const currentCreator = refreshSharingTip(
            sharingTipId,
            sharingData,
            recipientInfo,
            newSharingTipsPassedByReferenceAndModified,
            infoBar,
            getInfoBarCreator,
            skipActionRecommendation,
            convertCloudyToClassicAction
        );
        if (currentCreator) {
            lastInfoBarMessageViewStateCreator = currentCreator;
        }
    }

    if (typeof lastInfoBarMessageViewStateCreator === 'function') {
        const infoBarMessageViewState: InfoBarMessageViewState = lastInfoBarMessageViewStateCreator(
            null
        );
        if (
            infoBarMessageViewState?.messageParts &&
            infoBarMessageViewState.messageParts.length > 0 &&
            typeof infoBarMessageViewState.messageParts[0] === 'string'
        ) {
            return infoBarMessageViewState.messageParts[0] as string;
        }
    } else if (
        typeof lastInfoBarMessageViewStateCreator === 'object' &&
        lastInfoBarMessageViewStateCreator.length > 0
    ) {
        return lastInfoBarMessageViewStateCreator[0];
    }

    return null;
}

function refreshSharingTip(
    sharingTipId: SharingTipId,
    sharingData: SharingData[],
    recipientInfo: SharingTipRecipientInfo[],
    newSharingTipsPassedByReferenceAndModified: string[],
    infoBar: InfoBarHostViewState,
    getInfoBarCreator: (
        sharingData: SharingData[],
        sharingTipId: SharingTipId,
        recipientInfo: SharingTipRecipientInfo[],
        skipActionRecommendation: boolean,
        convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
    ) => InfoBarMessageViewStateCreator | null,
    skipActionRecommendation: boolean,
    convertCloudyToClassicAction?: (id: ClientAttachmentId) => InfoBarCustomAction
): InfoBarMessageViewStateCreator {
    const viewStateCreator = getInfoBarCreator(
        sharingData,
        sharingTipId,
        recipientInfo,
        skipActionRecommendation,
        convertCloudyToClassicAction
    );

    removeInfoBarMessage(infoBar, [sharingTipId]);
    if (viewStateCreator) {
        addInfoBarMessage(infoBar, sharingTipId, viewStateCreator);

        newSharingTipsPassedByReferenceAndModified.push(sharingTipId);
    }

    return viewStateCreator;
}

function logSharingTipsDatapoints(infoBarIds: string[], newSharingTips: string[]) {
    const sharingTipIds = getSharingTipIdsList();
    const previousSharingTips = infoBarIds.filter(
        id => (<string[]>sharingTipIds).indexOf(id) !== -1
    );
    const addedSharingTips: string[] = newSharingTips.filter(
        id => previousSharingTips.indexOf(id) === -1
    );
    const removedSharingTips: string[] = previousSharingTips.filter(
        id => newSharingTips.indexOf(id) === -1
    );
    if (addedSharingTips.length !== 0) {
        logUsage('AttachmentSharingTipAdded', addedSharingTips);
    }
    if (removedSharingTips.length !== 0) {
        logUsage('AttachmentSharingTipsRemoved', removedSharingTips);
    }
}
